import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	TextField,
	styled,
	InputAdornment,
	Stack,
	Menu,
	MenuItem,
	Typography,
} from '@mui/material';

import { useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { debounce } from 'lodash';

import { ReactComponent as Plus } from 'common/assets/images/Plus.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { RootState } from 'common/store/AppStore';
import {
	setPromptSearchText,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import {
	setIsPromptModalOpen,
	setIsPromptEdit,
	setIsReloadTableData,
} from 'common/store/reducer/promptBuilderReducer';

import { ADD_NEW_PROMPT_BTN } from './PromptBuilderDetails/PromptBuilderConstants';
import PromptBuilderDetailLayout from './PromptBuilderDetails/PromptBuilderDetailLayout';
import PromptBuilderPromptsList from './PromptBuilderPromptsList';

import promptBuilderStyles from './PromptBuilderLayout.module.scss';

const { searchBorder } = promptBuilderStyles;

const StyledTextField = styled(TextField)({
	background: 'white',
	borderRadius: '4px',
	border: `1px solid ${searchBorder}`,
	width: '40%',
});

const PromptBuilderLayout = () => {
	/* Destructured variables */

	const dispatch = useDispatch();

	const { whiteColor } = promptBuilderStyles;

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const promptModalOpen: boolean = useSelector(
		(state: RootState) => state.promptBuilder.promptModalOpen,
	);

	const reloadTableData: boolean = useSelector(
		(state: RootState) => state.promptBuilder.reloadTableData,
	);

	const promptSearchText = useSelector(
		(state: RootState) => state.common.promptSearchText,
	);

	const onSearchInputChange = (event: any) => {
		const text = event?.target?.value;
		dispatch(setPromptSearchText(text));
	};

	const debouncedSearchHandler = debounce(onSearchInputChange, 500);
	/**
	 * @description method to close the modal
	 */
	const onCloseModalHandler = () => {
		dispatch(setIsPromptModalOpen(false));
		dispatch(setIsPromptEdit(false));
	};

	/**
	 * @description method to close the toast on umount of component
	 */
	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const resetHandler = () => dispatch(setIsReloadTableData(false));
	const addPromptHandler = () => {
		dispatch(setIsPromptModalOpen(true));
	};

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<div>
			<Stack
				direction="row"
				spacing={2}
				className={promptBuilderStyles['prompt-actions-wrapper']}
			>
				<StyledTextField
					className={promptBuilderStyles['prompt-search']}
					InputProps={{
						inputProps: { 'data-testid': 'prompt-search' },
						startAdornment: (
							<InputAdornment position="start">
								<SearchOutlined
									className={promptBuilderStyles['input-search-icon']}
								/>
							</InputAdornment>
						),
					}}
					fullWidth
					id="fullWidth"
					placeholder="Search for a Regulation"
					onChange={debouncedSearchHandler}
					defaultValue={promptSearchText}
				/>
				<Stack
					direction="row"
					spacing={2}
					className={promptBuilderStyles['prompt-actions-btn-grp']}
				>
					<ButtonComp
						btnType="secondary"
						data-testid="filter-new-prompt"
						disabled
					>
						Filters
					</ButtonComp>
					<ButtonComp
						btnType="primary"
						data-testid="add-new-prompt"
						clickHandler={handleClick}
						endIcon={
							open ? (
								<ExpandLessIcon data-testid="arrow-up-icon" />
							) : (
								<ExpandMoreIcon data-testid="arrow-down-icon" />
							)
						}
					>
						<Stack
							direction="row"
							spacing={2}
							display="flex"
							justifyContent="center"
							alignItems="center"
						>
							<Plus fill={whiteColor} />
							<Typography data-testid="add-new-btn">
								{ADD_NEW_PROMPT_BTN}
							</Typography>
						</Stack>
					</ButtonComp>
					<Menu
						anchorEl={anchorEl}
						open={open}
						onClose={handleClose}
						MenuListProps={{
							'aria-labelledby': 'create-new-button',
						}}
						sx={{
							'& .MuiPaper-root': {
								boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
								borderRadius: '8px',
							},
						}}
					>
						<MenuItem onClick={addPromptHandler}>
							<Typography>Standard prompt</Typography>
						</MenuItem>
						{/* <MenuItem onClick={handleClose}>
							<Typography>Custom prompt</Typography>
						</MenuItem> */}
					</Menu>
				</Stack>
			</Stack>
			<PromptBuilderPromptsList
				reload={reloadTableData}
				resetHandler={resetHandler}
			/>
			{promptModalOpen && (
				<PromptBuilderDetailLayout
					resetHandler={resetHandler}
					isModalOpen={promptModalOpen}
					onCloseModal={onCloseModalHandler}
				/>
			)}

			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</div>
	);
};

export default PromptBuilderLayout;
